import { useMemo } from 'react'
import { useNavigate } from 'react-router'
import { useCollection } from '@cloudscape-design/collection-hooks'
import Badge from '@cloudscape-design/components/badge'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import Cards from '@cloudscape-design/components/cards'
import Container from '@cloudscape-design/components/container'
import Header from '@cloudscape-design/components/header'
import Pagination from '@cloudscape-design/components/pagination'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Table from '@cloudscape-design/components/table'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { usePublishQueueFull, useApproveItem, useRejectItem } from '@/hooks/useContent'
import {
  useOutreachQueuePending,
  useApproveOutreachItem,
  useRejectOutreachItem,
} from '@/hooks/useOutreachQueuePending'
import { formatDate } from '@/utils/format'
import type { GqlPublishQueueItem, OutreachQueueItem } from '@/lib/types'

interface Props {
  clientId: string
}

// ─── Unified row shape ────────────────────────────────────────────────────────

type RowKind = 'Content' | 'Outreach'

interface QueueRow {
  id: string
  rowKind: RowKind
  title: string
  createdAt: string
  isContent: boolean
}

function toContentRow(item: GqlPublishQueueItem): QueueRow {
  const title =
    item.subject ??
    item.recipientName ??
    item.contentType ??
    '(untitled)'
  return {
    id: item.id,
    rowKind: 'Content',
    title,
    createdAt: item.createdAt,
    isContent: true,
  }
}

function toOutreachRow(item: OutreachQueueItem): QueueRow {
  return {
    id: item.id,
    rowKind: 'Outreach',
    title: item.subject,
    createdAt: item.createdAt,
    isContent: false,
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ExceptionQueueSection({ clientId }: Props) {
  const navigate = useNavigate()
  const isMobile = useMediaQuery('(max-width: 1023px)')
  const { items: contentItems, loading: contentLoading } = usePublishQueueFull(clientId)
  const { items: outreachItems, loading: outreachLoading } = useOutreachQueuePending(clientId)

  const { approve, loading: approvingContent } = useApproveItem()
  const { reject, loading: rejectingContent } = useRejectItem()
  const { approveOutreachItem, loading: approvingOutreach } = useApproveOutreachItem()
  const { rejectOutreachItem, loading: rejectingOutreach } = useRejectOutreachItem()

  const isMutating = approvingContent || rejectingContent || approvingOutreach || rejectingOutreach

  const pendingContent = useMemo(
    () => contentItems.filter((item) => item.status === 'pending_review'),
    [contentItems],
  )

  const allRows: QueueRow[] = useMemo(
    () => [
      ...pendingContent.map(toContentRow),
      ...outreachItems.map(toOutreachRow),
    ],
    [pendingContent, outreachItems],
  )

  const { items, collectionProps, paginationProps } = useCollection(allRows, {
    pagination: { pageSize: 10 },
    sorting: {},
  })

  const loading = contentLoading || outreachLoading

  if (isMobile) {
    return (
      <Container
        header={
          <Header
            variant="h2"
            description="Posts and emails that need your review before publishing"
            actions={
              <Button
                variant="link"
                onClick={() => navigate(`/workspace/clients/${clientId}/queue`)}
              >
                View all in Queue
              </Button>
            }
          >
            Exception Queue
          </Header>
        }
      >
        <SpaceBetween size="m">
          <Cards<QueueRow>
            {...collectionProps}
            items={items}
            loading={loading}
            loadingText="Loading queue..."
            cardsPerRow={[{ cards: 1 }]}
            cardDefinition={{
              header: (row) => (
                <Box variant="strong">{row.title}</Box>
              ),
              sections: [
                {
                  id: 'meta',
                  content: (row) => (
                    <SpaceBetween direction="horizontal" size="s">
                      <Badge color={row.rowKind === 'Content' ? 'blue' : 'grey'}>
                        {row.rowKind}
                      </Badge>
                      <Box>{formatDate(row.createdAt)}</Box>
                    </SpaceBetween>
                  ),
                },
                {
                  id: 'actions',
                  content: (row) => (
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button
                        variant="primary"
                        disabled={isMutating}
                        onClick={() => {
                          if (row.isContent) {
                            void approve(clientId, row.id)
                          } else {
                            void approveOutreachItem(clientId, row.id)
                          }
                        }}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="normal"
                        disabled={isMutating}
                        onClick={() => {
                          if (row.isContent) {
                            void reject(clientId, row.id)
                          } else {
                            void rejectOutreachItem(clientId, row.id)
                          }
                        }}
                      >
                        Reject
                      </Button>
                    </SpaceBetween>
                  ),
                },
              ],
            }}
            empty={
              <Box color="text-body-secondary" textAlign="center" padding="xl">
                No items require review.
              </Box>
            }
          />
          <Pagination {...paginationProps} />
        </SpaceBetween>
      </Container>
    )
  }

  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Posts and emails that need your review before publishing"
          actions={
            <Button
              variant="link"
              onClick={() => navigate(`/workspace/clients/${clientId}/queue`)}
            >
              View all in Queue
            </Button>
          }
        >
          Exception Queue
        </Header>
      }
    >
      <Table
          {...collectionProps}
          items={items}
          columnDefinitions={[
            {
              id: 'type',
              header: 'Type',
              cell: (row) => row.rowKind,
              sortingField: 'rowKind',
            },
            {
              id: 'title',
              header: 'Title / Subject',
              cell: (row) => row.title,
              sortingField: 'title',
            },
            {
              id: 'createdAt',
              header: 'Created',
              cell: (row) => formatDate(row.createdAt),
              sortingField: 'createdAt',
            },
            {
              id: 'actions',
              header: 'Actions',
              cell: (row) => (
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    variant="primary"
                    disabled={isMutating}
                    onClick={() => {
                      if (row.isContent) {
                        void approve(clientId, row.id)
                      } else {
                        void approveOutreachItem(clientId, row.id)
                      }
                    }}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="normal"
                    disabled={isMutating}
                    onClick={() => {
                      if (row.isContent) {
                        void reject(clientId, row.id)
                      } else {
                        void rejectOutreachItem(clientId, row.id)
                      }
                    }}
                  >
                    Reject
                  </Button>
                </SpaceBetween>
              ),
            },
          ]}
          empty={
            <Box color="text-body-secondary" textAlign="center" padding="xl">
              No items require review.
            </Box>
          }
          pagination={<Pagination {...paginationProps} />}
        />
    </Container>
  )
}
